/* ==========================================================================
   Sticky Product Bar  ·  assets/sticky-product-bar.js
   --------------------------------------------------------------------------
   Behaviour for snippets/sticky-product-bar.liquid.

   Design principles (see architecture analysis):
     • Reflects the real product form; never owns cart or variant logic.
     • Add to Cart / Buy Now  → forwarded to the real product-form buttons,
       reusing product-form.js entirely (AJAX, drawer, bubble, errors, spinner).
     • Quantity               → two-way mirror with the form-associated
       #Quantity-{section} input (which is what actually posts).
     • Variant                → written back into the real <variant-radios>,
       letting VariantSelects resolve everything.
     • Price / stock / state  → synced from the theme's `variantChange` event
       (no extra network requests — we reuse the html it already fetched).

   No jQuery. No external libs. Passive where relevant. IntersectionObserver
   (no scroll polling). Selectors cached. Lazy — nothing runs until the element
   connects.
   ========================================================================== */

if (!customElements.get('sticky-product-bar')) {
  customElements.define(
    'sticky-product-bar',
    class StickyProductBar extends HTMLElement {
      constructor() {
        super();

        // ── Configuration from data-attributes ────────────────────────────
        this.section = this.dataset.section;
        this.formId = this.dataset.formId;
        this.enableBuyNow = this.dataset.enableBuyNow === 'true';
        this.designMode =
          this.dataset.designMode === 'true' ||
          (window.Shopify && window.Shopify.designMode);

        // ── Reentrancy guard for the quantity two-way mirror ──────────────
        this._syncingQty = false;

        // ── Bound handlers (stable refs so we can remove them cleanly) ─────
        this._onIntersect = this._onIntersect.bind(this);
      }

      /* -------------------------------------------------------------------- */
      /*  Lifecycle                                                           */
      /* -------------------------------------------------------------------- */
      connectedCallback() {
        // Cache the sticky-bar's own controls.
        this.inner = this.querySelector('.spb__inner');
        this.addButton = this.querySelector('.spb__button--add');
        this.addButtonText = this.addButton
          ? this.addButton.querySelector('.spb__button-text')
          : null;
        this.spinner = this.querySelector('.spb__spinner');
        this.buyNowButton = this.querySelector('.spb__button--buy');
        this.priceEl = document.getElementById(`StickyPrice-${this.section}`);
        this.variantName = document.getElementById(`StickyVariantName-${this.section}`);
        this.variantSelect = document.getElementById(`StickyVariantSelect-${this.section}`);
        this.image = document.getElementById(`StickyImage-${this.section}`);
        this.qtyInput = document.getElementById(`StickyQuantity-${this.section}`);
        this.qtyMinus = this.querySelector('.spb__qty-button[name="minus"]');
        this.qtyPlus = this.querySelector('.spb__qty-button[name="plus"]');

        // Cache the REAL product-form elements we integrate with.
        this.realForm = document.getElementById(this.formId);
        this.realSubmit = document.getElementById(`ProductSubmitButton-${this.section}`);
        this.realQtyInput = document.getElementById(`Quantity-${this.section}`);
        this.picker =
          document.getElementById(`variant-radios-${this.section}`) ||
          document.querySelector(`variant-radios[data-section="${this.section}"]`);

        // Without a real submit button there is nothing to sticky — bail out.
        if (!this.realSubmit) return;

        // JS is available: drop the [hidden] attribute so CSS transforms drive
        // visibility from here on (no-js users keep it hidden via .no-js-hidden).
        this.removeAttribute('hidden');

        this._bindEvents();
        this._subscribe();
        this._initState();
        this._observe();

        // In the Theme Editor, force the bar visible so it can be styled.
        if (this.designMode) {
          this.classList.add('spb--design-preview');
          this._show();
        }
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
        if (this._variantUnsub) this._variantUnsub();
        if (this._cartUpdateUnsub) this._cartUpdateUnsub();
        if (this._cartErrorUnsub) this._cartErrorUnsub();
      }

      /* -------------------------------------------------------------------- */
      /*  Visibility — IntersectionObserver on the real ATC                   */
      /* -------------------------------------------------------------------- */
      _observe() {
        // Observe the real buy-buttons area. Show the bar ONLY once that area
        // has scrolled ABOVE the viewport (not while it is still below the fold).
        this._target =
          this.realSubmit.closest('.product-form__buttons') || this.realSubmit;

        this._observer = new IntersectionObserver(this._onIntersect, {
          root: null,
          threshold: 0,
        });
        this._observer.observe(this._target);
      }

      _onIntersect(entries) {
        const entry = entries[0];
        if (this.designMode) return; // never auto-hide in the editor

        if (entry.isIntersecting) {
          this._hide();
        } else if (entry.boundingClientRect.top < 0) {
          // Scrolled past the ATC → reveal. (Below-the-fold ATC keeps it hidden.)
          this._show();
        } else {
          this._hide();
        }
      }

      _show() {
        if (this._visible) return;
        this._visible = true;
        this.classList.add('spb--visible');
        this.setAttribute('aria-hidden', 'false');
      }

      _hide() {
        if (!this._visible && this._visible !== undefined) return;
        this._visible = false;
        this.classList.remove('spb--visible');
        this.setAttribute('aria-hidden', 'true');
      }

      /* -------------------------------------------------------------------- */
      /*  Event wiring                                                        */
      /* -------------------------------------------------------------------- */
      _bindEvents() {
        // Add to Cart → forward to the real submit button (reuse product-form.js).
        if (this.addButton) {
          this.addButton.addEventListener('click', (e) => {
            e.preventDefault();
            this._onAddToCart();
          });
        }

        // Buy Now → real dynamic-checkout button, or add-then-checkout fallback.
        if (this.buyNowButton) {
          this.buyNowButton.addEventListener('click', (e) => {
            e.preventDefault();
            this._onBuyNow();
          });
        }

        // Variant → write selection back into the real picker.
        if (this.variantSelect && this.picker) {
          this.variantSelect.addEventListener('change', () => this._onVariantSelect());
        }

        // Quantity: sticky stepper + input → mirror to the real input.
        if (this.qtyInput && this.realQtyInput) {
          this.qtyInput.addEventListener('change', () => this._pushQtyToReal());
          if (this.qtyMinus) {
            this.qtyMinus.addEventListener('click', () => this._stepQty(-1));
          }
          if (this.qtyPlus) {
            this.qtyPlus.addEventListener('click', () => this._stepQty(1));
          }

          // Real input → sticky (covers theme updates + user edits in the form).
          this.realQtyInput.addEventListener('change', () => {
            if (this._syncingQty) return;
            this._syncQtyFromReal();
          });
        }
      }

      _subscribe() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this._onVariantChange(event);
        });
        this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this._setLoading(false);
        });
        this._cartErrorUnsub = subscribe(PUB_SUB_EVENTS.cartError, () => {
          this._setLoading(false);
        });
      }

      /* -------------------------------------------------------------------- */
      /*  Add to Cart / Buy Now                                               */
      /* -------------------------------------------------------------------- */
      _onAddToCart() {
        if (!this.realSubmit || this.realSubmit.hasAttribute('disabled')) return;
        this._setLoading(true);
        // Forwarding the click runs the real form's submit pipeline in full.
        this.realSubmit.click();
      }

      _onBuyNow() {
        if (!this.realForm || this.realSubmit.hasAttribute('disabled')) return;

        // Preferred path: click the real accelerated-checkout button.
        const dynamicBtn = this.realForm.querySelector('.shopify-payment-button__button');
        if (dynamicBtn) {
          dynamicBtn.click();
          return;
        }

        // Fallback (dynamic checkout disabled): add to cart, then go to checkout.
        this._setLoading(true);
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = new FormData(this.realForm);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this._setLoading(false);
              return;
            }
            const root =
              (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            window.location.href = `${root}checkout`;
          })
          .catch(() => this._setLoading(false));
      }

      _setLoading(isLoading) {
        if (!this.addButton) return;
        this.addButton.classList.toggle('loading', isLoading);
        this.addButton.toggleAttribute('aria-disabled', isLoading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      }

      /* -------------------------------------------------------------------- */
      /*  Variant sync                                                        */
      /* -------------------------------------------------------------------- */

      // Sticky <select> changed → apply its variant's option values to the real
      // picker, then dispatch one `change` so VariantSelects resolves everything.
      _onVariantSelect() {
        const variantId = parseInt(this.variantSelect.value, 10);
        const variant = this._getVariants().find((v) => v.id === variantId);
        if (!variant || !this.picker) return;

        const groups = this.picker.querySelectorAll('.product-form__input');
        groups.forEach((group, index) => {
          const value = variant.options[index];
          if (value == null) return;

          const radio = group.querySelector(
            `input[type="radio"][value="${this._cssEscape(value)}"]`
          );
          if (radio) {
            radio.checked = true;
            return;
          }
          const select = group.querySelector('select');
          if (select) select.value = value;
        });

        this.picker.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Theme published a variant change → reflect it in the sticky bar.
      // We reuse the html the theme already fetched: no extra request.
      _onVariantChange(event) {
        if (!event || !event.data || event.data.sectionId !== this.section) return;
        const { html, variant } = event.data;

        // Price (+ compare-at + discount badge) via the reused price snippet.
        if (this.priceEl && html) {
          const priceSource = html.getElementById(`price-${this.section}`);
          if (priceSource) this.priceEl.innerHTML = priceSource.innerHTML;
        }

        // Add-to-Cart state mirrors the freshly rendered real button.
        if (html) {
          const btnSource = html.getElementById(`ProductSubmitButton-${this.section}`);
          if (btnSource) {
            const disabled = btnSource.hasAttribute('disabled');
            this._setAddDisabled(disabled);
            const label = btnSource.querySelector('span');
            if (label && this.addButtonText) {
              this.addButtonText.textContent = label.textContent.trim();
            }
          }
        }

        if (variant) {
          if (this.variantName) this.variantName.textContent = variant.title;
          if (this.variantSelect) this.variantSelect.value = variant.id;
          if (this.image && variant.featured_image && variant.featured_image.src) {
            this.image.src = this._resizeImage(variant.featured_image.src, 200);
          }
        }

        // Quantity boundaries may have changed with the variant.
        this._syncQtyFromReal();
      }

      _setAddDisabled(disabled) {
        if (this.addButton) this.addButton.toggleAttribute('disabled', disabled);
        if (this.buyNowButton) this.buyNowButton.toggleAttribute('disabled', disabled);
      }

      _getVariants() {
        if (this._variants) return this._variants;
        const script = this.picker
          ? this.picker.querySelector('script[type="application/json"]')
          : null;
        try {
          this._variants = script ? JSON.parse(script.textContent) : [];
        } catch (e) {
          this._variants = [];
        }
        return this._variants;
      }

      /* -------------------------------------------------------------------- */
      /*  Quantity sync                                                       */
      /* -------------------------------------------------------------------- */

      // Copy min/max/step/value from the real (authoritative) input to sticky.
      _syncQtyFromReal() {
        if (!this.qtyInput || !this.realQtyInput) return;
        ['min', 'max', 'step'].forEach((attr) => {
          const value = this.realQtyInput.getAttribute(attr);
          if (value !== null) {
            this.qtyInput.setAttribute(attr, value);
          } else {
            this.qtyInput.removeAttribute(attr);
          }
        });
        this.qtyInput.value = this.realQtyInput.value;
        this._updateStepperState();
      }

      // Sticky input/stepper changed → push to the real, form-associated input
      // and dispatch `change` so <quantity-input> + <product-info> react.
      _pushQtyToReal() {
        if (!this.qtyInput || !this.realQtyInput) return;
        const clamped = this._clampQty(this.qtyInput.value);
        this.qtyInput.value = clamped;

        this._syncingQty = true;
        this.realQtyInput.value = clamped;
        this.realQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        this._syncingQty = false;

        this._updateStepperState();
      }

      _stepQty(direction) {
        if (!this.qtyInput) return;
        const step = parseInt(this.qtyInput.step, 10) || 1;
        const current = parseInt(this.qtyInput.value, 10) || 0;
        this.qtyInput.value = current + direction * step;
        this._pushQtyToReal();
      }

      _clampQty(rawValue) {
        const step = parseInt(this.qtyInput.step, 10) || 1;
        const min = this.qtyInput.min !== '' ? parseInt(this.qtyInput.min, 10) : step;
        const max = this.qtyInput.max !== '' ? parseInt(this.qtyInput.max, 10) : null;
        let value = parseInt(rawValue, 10);
        if (isNaN(value)) value = min;
        if (value < min) value = min;
        if (max !== null && value > max) value = max;
        return value;
      }

      _updateStepperState() {
        if (!this.qtyInput) return;
        const value = parseInt(this.qtyInput.value, 10);
        if (this.qtyMinus && this.qtyInput.min !== '') {
          this.qtyMinus.toggleAttribute('disabled', value <= parseInt(this.qtyInput.min, 10));
        }
        if (this.qtyPlus && this.qtyInput.max !== '') {
          this.qtyPlus.toggleAttribute('disabled', value >= parseInt(this.qtyInput.max, 10));
        }
      }

      /* -------------------------------------------------------------------- */
      /*  Initial state                                                       */
      /* -------------------------------------------------------------------- */
      _initState() {
        this._hide(); // ensure a clean hidden start
        if (this.realQtyInput) this._syncQtyFromReal();
      }

      /* -------------------------------------------------------------------- */
      /*  Small utilities                                                     */
      /* -------------------------------------------------------------------- */
      _resizeImage(src, width) {
        if (!src) return src;
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', width);
          return url.toString();
        } catch (e) {
          return src;
        }
      }

      _cssEscape(value) {
        if (window.CSS && typeof window.CSS.escape === 'function') {
          return window.CSS.escape(value);
        }
        // Minimal fallback: escape double-quotes for the attribute selector.
        return String(value).replace(/"/g, '\\"');
      }
    }
  );
}
